import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  LogOut,
  Link2,
  MessageCircle,
  RefreshCw,
  Send,
  Smartphone,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { PillButton } from '../clients/PillButton';
import { extractErrorMessage } from '../../lib/errors';
import {
  connectWhatsapp,
  disconnectWhatsapp,
  fetchWhatsappStatus,
  fetchWhatsappWebhookInfo,
  sendWhatsappTest,
  setupWhatsappWebhook,
  type WebhookInfo,
} from '../../lib/whatsapp-api';

/**
 * Card de WhatsApp no /settings — UX completa:
 *  - Mostra status atual (configurado? conectado? qual número?)
 *  - Botão "Conectar" → backend cria/conecta a instância e devolve QR
 *  - QR Code aparece inline; polling a cada 2s pra detectar quando conectar
 *  - Quando conectado: mostra número + botão "Desconectar"
 */
export function WhatsappConnectCard(): JSX.Element {
  const qc = useQueryClient();
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);

  const statusQ = useQuery({
    queryKey: ['whatsapp', 'status'],
    queryFn: fetchWhatsappStatus,
    refetchInterval: polling ? 2_000 : false,
    refetchOnWindowFocus: false,
  });

  const connectM = useMutation({
    mutationFn: connectWhatsapp,
    onSuccess: (data) => {
      if (data.state === 'open') {
        setQrCode(null);
        setPolling(false);
        toast.success('WhatsApp já estava conectado.');
        void qc.invalidateQueries({ queryKey: ['whatsapp', 'status'] });
      } else if (data.qrCode) {
        setQrCode(data.qrCode);
        setPolling(true);
      } else {
        toast.error('Evolution não retornou QR Code.');
      }
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err, 'Não consegui falar com o Evolution.'));
    },
  });

  const disconnectM = useMutation({
    mutationFn: disconnectWhatsapp,
    onSuccess: () => {
      setQrCode(null);
      setPolling(false);
      toast.success('WhatsApp desconectado.');
      void qc.invalidateQueries({ queryKey: ['whatsapp', 'status'] });
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err, 'Não consegui desconectar.'));
    },
  });

  const webhookInfoQ = useQuery({
    queryKey: ['whatsapp', 'webhook'],
    queryFn: fetchWhatsappWebhookInfo,
    enabled: Boolean(statusQ.data?.connected),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const webhookM = useMutation({
    mutationFn: setupWhatsappWebhook,
    onSuccess: (data) => {
      if (data.ok) {
        toast.success(`Webhook registrado em ${data.webhookUrl}`);
        void qc.invalidateQueries({ queryKey: ['whatsapp', 'webhook'] });
      } else {
        toast.error(data.errorMessage ?? 'Não consegui registrar o webhook.');
      }
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err, 'Falha ao registrar webhook.'));
    },
  });

  const [testPhone, setTestPhone] = useState('');
  const testM = useMutation({
    mutationFn: () => sendWhatsappTest({ phone: testPhone }),
    onSuccess: (data) => {
      if (data.ok) {
        toast.success(`Mensagem enviada pra ${data.sentTo}. Confere o zap aí!`);
      } else {
        toast.error(data.errorMessage ?? 'Falha no envio.');
      }
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err, 'Falha no envio.'));
    },
  });

  // Quando o polling detecta connected=true, para o polling e limpa QR
  useEffect(() => {
    if (statusQ.data?.connected) {
      if (polling) {
        toast.success('WhatsApp conectado!');
      }
      setQrCode(null);
      setPolling(false);
    }
  }, [statusQ.data?.connected, polling]);

  const status = statusQ.data;

  // ESTADO 1: backend não tem envs configuradas
  if (status && !status.configured) {
    return (
      <Frame>
        <Header
          title="WhatsApp não configurado no servidor"
          subtitle="Avise o admin do servidor pra setar EVOLUTION_URL, EVOLUTION_API_KEY e EVOLUTION_INSTANCE no .env do API."
          icon={<AlertTriangle size={14} strokeWidth={1.8} className="text-warn" />}
        />
      </Frame>
    );
  }

  // ESTADO 2: conectado
  if (status?.connected) {
    return (
      <Frame>
        <Header
          title="WhatsApp conectado"
          subtitle={
            status.profileName
              ? `${status.profileName} · ${formatPhone(status.phoneNumber)}`
              : (formatPhone(status.phoneNumber) ?? 'Conectado')
          }
          icon={<CheckCircle2 size={14} strokeWidth={1.8} className="text-good" />}
        />
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-[6px] border border-good/30 bg-good/10 px-2 py-1 text-[11px] font-medium text-good">
            <Smartphone size={11} strokeWidth={2} />
            instância: {status.instance}
          </span>
          <PillButton
            icon={<Link2 size={13} strokeWidth={2} />}
            onClick={() => webhookM.mutate()}
            disabled={webhookM.isPending}
          >
            {webhookM.isPending
              ? 'Registrando…'
              : webhookInfoQ.data?.pointsToUs
                ? 'Re-registrar webhook'
                : 'Registrar webhook'}
          </PillButton>
          <PillButton
            icon={<LogOut size={13} strokeWidth={2} />}
            onClick={() => disconnectM.mutate()}
            disabled={disconnectM.isPending}
            danger
          >
            Desconectar
          </PillButton>
        </div>

        <WebhookStateBlock info={webhookInfoQ.data} loading={webhookInfoQ.isFetching} />

        {/* Envio de mensagem de teste */}
        <div className="mt-3 rounded-[10px] border border-line bg-panel-2/40 p-3">
          <div className="mb-2 flex items-center gap-2 text-[12px] font-medium text-ink">
            <Send size={12} strokeWidth={1.8} />
            Enviar mensagem de teste
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="tel"
              inputMode="numeric"
              placeholder="ex: 16999010292"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value.replace(/\D/g, ''))}
              maxLength={15}
              className="h-8 flex-1 min-w-[180px] rounded-[6px] border border-line bg-panel px-2 text-[12px] text-ink-2 placeholder:text-muted-3 focus:border-good/50 focus:outline-none"
            />
            <PillButton
              icon={<Send size={12} strokeWidth={2} />}
              onClick={() => testM.mutate()}
              disabled={testM.isPending || testPhone.replace(/\D/g, '').length < 10}
            >
              {testM.isPending ? 'Enviando…' : 'Enviar teste'}
            </PillButton>
          </div>
          <p className="mt-2 text-[11px] text-muted-2">
            Cola só números: DDD + número. Ex: <code className="font-mono">16999010292</code>. Vai
            mandar uma mensagem de confirmação no zap desse número.
          </p>
        </div>
      </Frame>
    );
  }

  // ESTADO 3: tem QR exibindo
  if (qrCode) {
    return (
      <Frame>
        <Header
          title="Escaneie o QR Code"
          subtitle="WhatsApp do celular → Configurações → Aparelhos Conectados → Conectar um aparelho."
          icon={<MessageCircle size={14} strokeWidth={1.8} />}
        />
        <div className="flex flex-col items-center gap-3">
          <div className="rounded-[10px] border border-line bg-white p-3">
            <img src={qrCode} alt="QR Code WhatsApp" width={240} height={240} />
          </div>
          <p className="m-0 text-center text-[12px] text-muted">
            O QR expira em ~30s. Estamos checando a conexão automaticamente.
          </p>
          <div className="flex items-center gap-2">
            <PillButton
              icon={
                <RefreshCw
                  size={13}
                  strokeWidth={2}
                  className={connectM.isPending ? 'animate-spin' : ''}
                />
              }
              onClick={() => connectM.mutate()}
              disabled={connectM.isPending}
            >
              Gerar novo QR
            </PillButton>
            <PillButton
              onClick={() => {
                setQrCode(null);
                setPolling(false);
              }}
            >
              Cancelar
            </PillButton>
          </div>
        </div>
      </Frame>
    );
  }

  // ESTADO 4: configurado mas desconectado, sem QR ainda
  return (
    <Frame>
      <Header
        title="Conectar WhatsApp"
        subtitle={
          status?.instance
            ? `Instância "${status.instance}" pronta. Clique pra gerar o QR Code e parear o número.`
            : 'Conecte o número do WhatsApp ao Control Center.'
        }
        icon={<MessageCircle size={14} strokeWidth={1.8} />}
      />
      <PillButton
        icon={<MessageCircle size={13} strokeWidth={2} />}
        onClick={() => connectM.mutate()}
        disabled={connectM.isPending}
        solid
      >
        {connectM.isPending ? 'Gerando QR…' : 'Conectar WhatsApp'}
      </PillButton>
    </Frame>
  );
}

function Frame({ children }: { children: React.ReactNode }): JSX.Element {
  return <div className="space-y-4">{children}</div>;
}

function WebhookStateBlock({
  info,
  loading,
}: {
  info: WebhookInfo | undefined;
  loading: boolean;
}): JSX.Element {
  if (loading && !info) {
    return (
      <div className="rounded-[10px] border border-line bg-panel-2/40 p-3 text-[11px] text-muted-2">
        Verificando webhook na Evolution…
      </div>
    );
  }
  if (!info) return <></>;

  if (info.pointsToUs && info.enabled) {
    return (
      <div className="rounded-[10px] border border-good/30 bg-good/10 p-3 text-[12px] text-ink-2">
        <div className="flex items-center gap-1 text-good">
          <CheckCircle2 size={12} strokeWidth={2} />
          <strong className="text-[12px]">Webhook conectado e ativo</strong>
        </div>
        <p className="m-0 mt-1 text-[11px] text-muted-2 break-all">
          Apontando pra <code className="font-mono">{info.currentUrl}</code> · Eventos:{' '}
          {info.events.join(', ') || 'nenhum'}
        </p>
      </div>
    );
  }

  if (info.registered && !info.pointsToUs) {
    return (
      <div className="rounded-[10px] border border-warn/30 bg-warn/10 p-3 text-[12px] text-ink-2">
        <div className="flex items-center gap-1 text-warn">
          <AlertTriangle size={12} strokeWidth={2} />
          <strong className="text-[12px]">Webhook apontando pra OUTRO lugar</strong>
        </div>
        <p className="m-0 mt-1 text-[11px] text-muted-2 break-all">
          Atual: <code className="font-mono">{info.currentUrl}</code>
          {info.expectedUrl && (
            <>
              <br />
              Esperado: <code className="font-mono">{info.expectedUrl}</code>
            </>
          )}
        </p>
        <p className="m-0 mt-2 text-[11px] text-muted-2">
          Clica em &ldquo;Registrar webhook&rdquo; pra apontar pra cá. Isso vai sobrescrever o
          atual.
        </p>
      </div>
    );
  }

  if (info.registered && !info.enabled) {
    return (
      <div className="rounded-[10px] border border-warn/30 bg-warn/10 p-3 text-[12px] text-ink-2">
        <div className="flex items-center gap-1 text-warn">
          <AlertTriangle size={12} strokeWidth={2} />
          <strong className="text-[12px]">Webhook registrado mas DESABILITADO</strong>
        </div>
        <p className="m-0 mt-1 text-[11px] text-muted-2">
          Clica em &ldquo;Registrar webhook&rdquo; pra reativar.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-[10px] border border-line bg-panel-2/40 p-3 text-[12px] text-ink-2">
      <div className="flex items-center gap-1 text-muted-2">
        <AlertTriangle size={12} strokeWidth={2} />
        <strong className="text-[12px]">Nenhum webhook registrado</strong>
      </div>
      <p className="m-0 mt-1 text-[11px] text-muted-2">
        Mensagens recebidas no zap não vão chegar aqui até você clicar em &ldquo;Registrar
        webhook&rdquo;.
      </p>
    </div>
  );
}

function Header({
  title,
  subtitle,
  icon,
}: {
  title: string;
  subtitle: string;
  icon: JSX.Element;
}): JSX.Element {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-[2px] flex h-7 w-7 items-center justify-center rounded-[8px] bg-panel-3">
        {icon}
      </span>
      <div className="flex-1">
        <h4 className="m-0 text-[13px] font-semibold text-ink">{title}</h4>
        <p className="mt-[2px] text-[12px] text-muted">{subtitle}</p>
      </div>
    </div>
  );
}

function formatPhone(raw: string | null): string | null {
  if (!raw) return null;
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 13) {
    return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
  }
  if (digits.length === 12) {
    return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 8)}-${digits.slice(8)}`;
  }
  return raw;
}
